// Phase 3 — Pricing events feed for the OptiGuard graph + insights.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PartsPricingEvent {
  id: string;
  shop_id: string;
  parts_line_id: string | null;
  category: string | null;
  unit_cost: number | null;
  suggested_sell: number | null;
  actual_sell: number | null;
  delta_pct: number | null;
  // Phase 6B-Hardening Item 4: union aligned with DB CHECK constraint, which
  // accepts the original 5 plus the broader pricing-event lifecycle values.
  source:
    | 'manual_override'
    | 'matrix'
    | 'flat'
    | 'optiguard'
    | 'fallback'
    | 'manual'
    | 'imported'
    | 'other'
    | 'system';
  is_canned: boolean;
  created_at: string;
}

export interface UsePartsPricingEventsArgs {
  shopId: string | undefined;
  from?: Date | null;
  to?: Date | null;
  category?: string | null;
  source?: PartsPricingEvent['source'] | null;
  excludeCanned?: boolean;       // default true
  overridesOnly?: boolean;       // default false
  limit?: number;                // default 1000
  /**
   * CSI-P2: opt-in filter that excludes lifecycle audit events
   * (`event_type IS NOT NULL`) so callers consume only the sparse
   * trigger-recorded events used for aggregate analytics.
   *
   * The CSI-P1 dual-event design has both the DMC-J1 trigger AND the
   * `useReapplyMatrixPricing` hook write into `parts_pricing_events`
   * for each manual-override-removed reapply. The trigger event is
   * sparse (`event_type IS NULL`); the hook event is rich
   * (`event_type='manual_override_removed'`, plus
   * `previous_value`/`new_value`/`actor_user_id` lifecycle fields).
   * Pre-CSI-P2, aggregate-stat consumers (OverrideInsightsCard +
   * useOptiGuardSuggestion) read BOTH rows for the same reapply,
   * giving 2× weight to override-removal events. This flag eliminates
   * that bias by filtering to the canonical trigger-event population.
   *
   * Default false to preserve the existing contract for any consumer
   * that legitimately wants the full event stream.
   */
  aggregateOnly?: boolean;
}

export function usePartsPricingEvents(args: UsePartsPricingEventsArgs) {
  const {
    shopId, from, to, category, source,
    excludeCanned = true, overridesOnly = false, limit = 1000,
    aggregateOnly = false,
  } = args;

  return useQuery({
    queryKey: [
      'parts-pricing-events', shopId,
      from?.toISOString() ?? null, to?.toISOString() ?? null,
      category ?? null, source ?? null, excludeCanned, overridesOnly, limit,
      // CSI-P2: include in queryKey so toggling aggregateOnly produces
      // a distinct cache entry — important because OverrideInsightsCard
      // (aggregateOnly=true) and any future caller passing
      // aggregateOnly=false would share the same key without it.
      aggregateOnly,
    ] as const,
    enabled: !!shopId,
    queryFn: async (): Promise<PartsPricingEvent[]> => {
      let q = (supabase as any)
        .from('parts_pricing_events')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (from) q = q.gte('created_at', from.toISOString());
      if (to) q = q.lte('created_at', to.toISOString());
      if (category) q = q.eq('category', category);
      if (source) q = q.eq('source', source);
      if (excludeCanned) q = q.eq('is_canned', false);
      if (overridesOnly) q = q.eq('source', 'manual_override');
      // CSI-P2: filter out lifecycle events so aggregate-stat consumers
      // count each reapply once (the trigger event), not twice (trigger
      // event + manual lifecycle event with the same actual_sell).
      if (aggregateOnly) q = q.is('event_type', null);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PartsPricingEvent[];
    },
  });
}
